import {
  Card,
  DatePicker,
  Flex,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const columns = [
  {
    title: "Data/Hora",
    dataIndex: "timestamp",
    key: "timestamp",
    width: 180,
    render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm:ss") : "-"),
    sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    defaultSortOrder: "descend",
  },
  {
    title: "E-mail",
    dataIndex: "email",
    key: "email",
    width: 220,
  },
  {
    title: "Nome",
    key: "name",
    width: 180,
    render: (_, r) =>
      [r.first_name, r.last_name].filter(Boolean).join(" ") || "-",
  },
  {
    title: "IP",
    dataIndex: "ip_address",
    key: "ip_address",
    width: 140,
    render: (v) => v || "-",
  },
  {
    title: "Evento",
    dataIndex: "event_type",
    key: "event_type",
    width: 160,
    render: (v) =>
      v === "LOGIN_SUCCESS" ? (
        <Tag color="green">Login bem-sucedido</Tag>
      ) : (
        <Tag color="red">Falha de login</Tag>
      ),
    filters: [
      { text: "Login bem-sucedido", value: "LOGIN_SUCCESS" },
      { text: "Falha de login", value: "LOGIN_FAILED" },
    ],
    onFilter: (value, record) => record.event_type === value,
  },
  {
    title: "Navegador / Dispositivo",
    dataIndex: "user_agent",
    key: "user_agent",
    ellipsis: true,
    render: (v) => (
      <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 260 }}>
        {v || "-"}
      </Text>
    ),
  },
];

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "Todos os eventos" },
  { value: "LOGIN_SUCCESS", label: "Login bem-sucedido" },
  { value: "LOGIN_FAILED", label: "Falha de login" },
];

export default function AccessLog() {
  const today = dayjs();
  const [dateRange, setDateRange] = useState([today.subtract(7, "day"), today]);
  const [pickerDates, setPickerDates] = useState(null);
  const [eventType, setEventType] = useState("");
  const [includeSuperadmins, setIncludeSuperadmins] = useState(false);

  const { getAccessLogs } = useApi();

  const { data, isLoading } = useQuery({
    queryKey: [
      "accessLogs",
      dateRange[0]?.format("YYYY-MM-DD"),
      dateRange[1]?.format("YYYY-MM-DD"),
      eventType,
      includeSuperadmins,
    ],
    queryFn: () =>
      getAccessLogs({
        start_date: dateRange[0]?.format("YYYY-MM-DD"),
        end_date: dateRange[1]?.format("YYYY-MM-DD"),
        event_type: eventType || undefined,
        include_superadmins: includeSuperadmins || undefined,
      }),
    enabled: Boolean(dateRange[0] && dateRange[1]),
    select: (res) => res?.data?.results ?? [],
    retry: false,
  });

  return (
    <Flex
      style={{
        width: "100%",
        maxWidth: 1200,
        margin: "0 auto",
        padding: "0 24px",
      }}
      vertical
      gap={24}>
      <Card
        title="Log de Acessos"
        styles={{ header: { backgroundColor: "var(--ouran-card-header-bg)" } }}>
        <Flex gap={16} wrap="wrap" style={{ marginBottom: 16 }}>
          <RangePicker
            value={pickerDates ?? dateRange}
            onCalendarChange={setPickerDates}
            onChange={(range) => {
              setDateRange(range ?? [null, null]);
              setPickerDates(null);
            }}
            onOpenChange={(open) => {
              if (open) setPickerDates([null, null]);
            }}
            format="DD/MM/YYYY"
            allowClear={false}
            style={{ minWidth: 260 }}
          />
          <Select
            options={EVENT_TYPE_OPTIONS}
            value={eventType}
            onChange={setEventType}
            style={{ minWidth: 200 }}
          />
          <Flex align="center" gap={8}>
            <Switch
              size="small"
              checked={includeSuperadmins}
              onChange={setIncludeSuperadmins}
            />
            <span
              style={{
                fontSize: 13,
                color: "var(--ouran-text-secondary, #666)",
              }}>
              Incluir superadmins
            </span>
          </Flex>
        </Flex>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>
    </Flex>
  );
}
